import { todayDateOnly } from "@/lib/dateOnly";

import type { EntryStatus, TimeEntry } from "./types";
import { UNRELEASED_STATUSES } from "./types";

/** "0.5h", "1.25h", "0h". */
export const formatHours = (hours: number): string => {
  const rounded = Math.round((hours || 0) * 100) / 100;
  return `${rounded}h`;
};

/** "1h 30m" for a running count of seconds. */
export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds / 60));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
};

export const formatMoney = (amount: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(amount || 0);

/** Dollars, with cents only when there are any ($42.50, $780). */
export const formatAmount = (amount: number): string => {
  const cents = Math.round((amount || 0) * 100) % 100 !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

export const isUnreleased = (status: EntryStatus): boolean =>
  UNRELEASED_STATUSES.has(status);

export const totalHours = (
  entries: readonly Pick<TimeEntry, "duration_hours">[]
): number =>
  Math.round(
    entries.reduce((sum, e) => sum + (e.duration_hours || 0), 0) * 100
  ) / 100;

/** Today in the browser's zone as YYYY-MM-DD. */
export const todayIso = todayDateOnly;

/** `iso` shifted by `days`, as YYYY-MM-DD. */
export const shiftDate = (iso: string, days: number): string => {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  return todayIso(date);
};

/** "Wednesday" for a YYYY-MM-DD. */
export const weekdayOf = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long" });
};

/** The Monday of the week holding `iso`. */
export const weekStart = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const offset = (date.getDay() + 6) % 7;
  return shiftDate(iso, -offset);
};
